import React, { useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Eye, Save, X, ArrowLeft,
  Search, FileText, Globe, Image as ImageIcon, LogOut
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_POST = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  meta_description: '',
  keywords: '',
  og_image: '',
  status: 'draft',
  reading_time: 5,
};

export default function BlogAdminPage() {
  const { currentTheme: t, theme } = useTheme();
  const { user, loading: authLoading, isAdmin, signInWithGoogle, signOut } = useAuth();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('list');

  useEffect(() => { if (isAdmin) fetchPosts(); }, [isAdmin]);

  // Auth gate
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: t.bg }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: t.accent }} />
          <p className="text-sm" style={{ color: t.textMuted }}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: t.bg, fontFamily: 'Inter, sans-serif' }}>
        <div className="max-w-md w-full mx-4 p-8 rounded-2xl border text-center" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
          <img
            src={theme === 'light'
              ? 'https://customer-assets.emergentagent.com/job_motivy-light/artifacts/1jtvp4vu_LOGO-FINAL-MOTIVY%20%281%29%20%281%29.png'
              : 'https://customer-assets.emergentagent.com/job_motivy-light/artifacts/y1xqcc0j_logo%20blanco%20%281%29.png'
            }
            alt="Motivy" className="h-10 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold mb-2" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>
            Blog Admin
          </h1>
          <p className="text-sm mb-8" style={{ color: t.textMuted }}>
            {user && !isAdmin
              ? `${user.email} no tiene permisos de administrador.`
              : 'Inicia sesión para administrar el blog.'}
          </p>
          {user && !isAdmin ? (
            <div className="space-y-3">
              <p className="text-xs" style={{ color: t.textMuted }}>Solo usuarios @motivy.co pueden acceder.</p>
              <button onClick={signOut} className="text-sm font-medium" style={{ color: t.accent }}>
                Cerrar sesión e intentar con otra cuenta
              </button>
            </div>
          ) : (
            <button
              onClick={signInWithGoogle}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-lg text-sm font-semibold border transition-all duration-200 w-full justify-center"
              style={{ background: t.cardBg, borderColor: t.borderColor, color: t.text }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = t.accent}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderColor}
            >
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Iniciar sesión con Google
            </button>
          )}
          <div className="mt-6">
            <Link to="/" className="text-xs" style={{ color: t.textMuted }}>← Volver al sitio</Link>
          </div>
        </div>
      </div>
    );
  }

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API}/blog/admin/all`);
      if (res.ok) setPosts(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (val) => {
    setForm(prev => ({
      ...prev,
      title: val,
      slug: !editing ? generateSlug(val) : prev.slug,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editing ? `${API}/blog/${editing}` : `${API}/blog`;
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        await fetchPosts();
        setActiveTab('list');
        setEditing(null);
        setForm(EMPTY_POST);
      }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    try {
      await fetch(`${API}/blog/${id}`, { method: 'DELETE' });
      await fetchPosts();
    } catch (e) { console.error(e); }
  };

  const startEdit = (post) => {
    setEditing(post.id);
    setForm({
      title: post.title,
      slug: post.slug,
      content: post.content || '',
      excerpt: post.excerpt || '',
      meta_description: post.meta_description || '',
      keywords: post.keywords || '',
      og_image: post.og_image || '',
      status: post.status || 'draft',
      reading_time: post.reading_time || 5,
    });
    setActiveTab('editor');
  };

  const filteredPosts = posts.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const inputStyle = {
    background: t.inputBg,
    color: t.text,
    border: `1px solid ${t.inputBorder}`,
    fontFamily: 'Inter, sans-serif',
  };

  return (
    <div className="min-h-screen" style={{ background: t.bg, fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: t.borderColor, background: t.navBg, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <ArrowLeft size={18} style={{ color: t.textMuted }} />
                <span className="text-sm" style={{ color: t.textMuted }}>Volver al sitio</span>
              </Link>
              <div className="w-px h-8" style={{ background: t.borderColor }} />
              <h1 className="text-lg font-bold" style={{ color: t.text, fontFamily: 'Manrope, sans-serif' }}>
                <FileText size={18} className="inline mr-2" style={{ color: t.accent }} />
                Blog Admin
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs hidden sm:inline" style={{ color: t.textMuted }}>{user?.email}</span>
              <button onClick={signOut} className="p-2 rounded-lg transition-colors duration-200" style={{ color: t.textMuted }}
                onMouseEnter={(e) => e.currentTarget.style.color = t.accent}
                onMouseLeave={(e) => e.currentTarget.style.color = t.textMuted}
                title="Cerrar sesión"
              >
                <LogOut size={18} />
              </button>
              {activeTab === 'list' && (
                <button
                  onClick={() => { setEditing(null); setForm(EMPTY_POST); setActiveTab('editor'); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
                  style={{ background: '#F05864' }}
                >
                  <Plus size={16} /> Nuevo artículo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'list' ? (
          <>
            {/* Search */}
            <div className="mb-6 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: t.textMuted }} />
              <input
                type="text"
                placeholder="Buscar artículos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg text-sm outline-none"
                style={inputStyle}
              />
            </div>

            {/* Posts List */}
            {loading ? (
              <p className="text-sm" style={{ color: t.textMuted }}>Cargando...</p>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-16">
                <FileText size={48} className="mx-auto mb-4" style={{ color: t.textMuted, opacity: 0.4 }} />
                <p className="text-lg font-medium mb-2" style={{ color: t.text }}>No hay artículos aún</p>
                <p className="text-sm mb-6" style={{ color: t.textMuted }}>Crea tu primer artículo optimizado para SEO</p>
                <button
                  onClick={() => { setEditing(null); setForm(EMPTY_POST); setActiveTab('editor'); }}
                  className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg text-sm font-medium text-white"
                  style={{ background: '#F05864' }}
                >
                  <Plus size={16} /> Crear artículo
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredPosts.map((post) => (
                  <div
                    key={post.id}
                    className="flex items-center justify-between p-4 rounded-xl border"
                    style={{ background: t.cardBg, borderColor: t.cardBorder }}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {post.og_image && (
                        <img src={post.og_image} alt="" className="w-16 h-12 rounded-lg object-cover shrink-0" />
                      )}
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold truncate" style={{ color: t.text }}>{post.title}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${post.status === 'published' ? 'text-green-400' : 'text-yellow-400'}`}
                            style={{ background: post.status === 'published' ? 'rgba(74,222,128,0.1)' : 'rgba(250,204,21,0.1)' }}>
                            {post.status === 'published' ? 'Publicado' : 'Borrador'}
                          </span>
                          <span className="text-xs" style={{ color: t.textMuted }}>
                            /{post.slug}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      <Link to={`/blog/${post.slug}`} target="_blank" className="p-2 rounded-lg transition-colors duration-200" style={{ color: t.textMuted }}
                        onMouseEnter={(e) => e.currentTarget.style.background = t.surfaceHover}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <Eye size={16} />
                      </Link>
                      <button onClick={() => startEdit(post)} className="p-2 rounded-lg transition-colors duration-200" style={{ color: t.textMuted }}
                        onMouseEnter={(e) => e.currentTarget.style.background = t.surfaceHover}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(post.id)} className="p-2 rounded-lg transition-colors duration-200" style={{ color: '#F05864' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(240,88,100,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          /* Editor */
          <div>
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => { setActiveTab('list'); setEditing(null); }} className="flex items-center gap-2 text-sm" style={{ color: t.textMuted }}>
                <ArrowLeft size={16} /> Volver a la lista
              </button>
              <div className="flex items-center gap-3">
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="px-3 py-2 rounded-lg text-sm outline-none"
                  style={inputStyle}
                >
                  <option value="draft">Borrador</option>
                  <option value="published">Publicado</option>
                </select>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.title}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white"
                  style={{ background: saving ? '#999' : '#F05864' }}
                >
                  <Save size={16} /> {saving ? 'Guardando...' : editing ? 'Actualizar' : 'Publicar'}
                </button>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-5">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: t.textMuted }}>Título del artículo *</label>
                  <input
                    type="text" value={form.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg text-lg font-semibold outline-none"
                    style={{ ...inputStyle, fontFamily: 'Manrope, sans-serif' }}
                    placeholder="Título de tu artículo..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: t.textMuted }}>Contenido (HTML)</label>
                  <textarea
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg text-sm outline-none resize-none"
                    style={{ ...inputStyle, minHeight: 400 }}
                    placeholder="<h2>Introducción</h2>\n<p>Escribe tu contenido aquí...</p>\n<h2>Sección 2</h2>\n<p>Más contenido...</p>"
                  />
                </div>
              </div>

              {/* SEO Sidebar */}
              <div className="space-y-5">
                <div className="p-5 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <h3 className="flex items-center gap-2 text-sm font-semibold mb-4" style={{ color: t.text }}>
                    <Globe size={16} style={{ color: t.featureIconColor }} /> SEO
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: t.textMuted }}>Slug (URL)</label>
                      <div className="flex items-center gap-1 text-xs mb-1" style={{ color: t.textMuted }}>/blog/</div>
                      <input
                        type="text" value={form.slug}
                        onChange={(e) => setForm({ ...form, slug: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: t.textMuted }}>
                        Meta descripción <span className="text-xs" style={{ color: form.meta_description.length > 160 ? '#F05864' : t.textMuted }}>({form.meta_description.length}/160)</span>
                      </label>
                      <textarea
                        value={form.meta_description}
                        onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                        style={inputStyle}
                        placeholder="Descripción para buscadores..."
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: t.textMuted }}>Keywords (separadas por coma)</label>
                      <input
                        type="text" value={form.keywords}
                        onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                        style={inputStyle}
                        placeholder="reconocimiento, bienestar, RRHH"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: t.textMuted }}>Extracto</label>
                      <textarea
                        value={form.excerpt}
                        onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                        style={inputStyle}
                        placeholder="Resumen breve del artículo..."
                      />
                    </div>
                  </div>
                </div>

                <div className="p-5 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <h3 className="flex items-center gap-2 text-sm font-semibold mb-4" style={{ color: t.text }}>
                    <ImageIcon size={16} style={{ color: t.featureIconColor }} /> Imagen
                  </h3>
                  <div className="space-y-3">
                    <input
                      type="text" value={form.og_image}
                      onChange={(e) => setForm({ ...form, og_image: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={inputStyle}
                      placeholder="URL de la imagen principal..."
                    />
                    {form.og_image && (
                      <img src={form.og_image} alt="Preview" className="w-full rounded-lg aspect-video object-cover" />
                    )}
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: t.textMuted }}>Tiempo de lectura (min)</label>
                      <input
                        type="number" value={form.reading_time}
                        onChange={(e) => setForm({ ...form, reading_time: parseInt(e.target.value) || 5 })}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                        style={inputStyle}
                        min={1}
                      />
                    </div>
                  </div>
                </div>

                {/* SEO Preview */}
                <div className="p-5 rounded-xl border" style={{ background: t.cardBg, borderColor: t.cardBorder }}>
                  <h3 className="text-xs font-semibold mb-3" style={{ color: t.textMuted }}>Vista previa en Google</h3>
                  <div className="space-y-1">
                    <p className="text-sm font-medium truncate" style={{ color: '#1a0dab' }}>
                      {form.title || 'Título del artículo'} | Blog Motivy
                    </p>
                    <p className="text-xs" style={{ color: '#006621' }}>
                      motivyapp.com/blog/{form.slug || 'tu-articulo'}
                    </p>
                    <p className="text-xs line-clamp-2" style={{ color: '#545454' }}>
                      {form.meta_description || 'Agrega una meta descripción para mejorar el SEO...'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
